import { useState } from "react";

/**
 * Modale "Demander une démo" — à déclencher depuis le bouton "Voir une démo →"
 * de Landing.jsx :  onClick={() => setShowDemoModal(true)}
 *
 * Backend attendu (à créer si absent) :
 *   POST /api/leads   { nom, email, entreprise, telephone, message, source, consentement_rgpd }
 *   -> insertion dans une table `leads` (réutilisable ensuite par le module Prospection IA / CRM)
 *   -> route publique, sans auth, avec une limite de débit basique (anti-spam)
 *
 * Remplace API_BASE par la constante déjà utilisée ailleurs dans l'app (ex. services/api.js)
 */

const API_BASE = "/api"; // ajuster si besoin (ex. import { api } from "../services/api")

export default function DemoRequestModal({ open, onClose }) {
  const [form, setForm] = useState({
    nom: "",
    email: "",
    entreprise: "",
    telephone: "",
    message: "",
    consentement_rgpd: false,
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");

  if (!open) return null;

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!form.consentement_rgpd) {
      setError("Merci de cocher la case de consentement pour être recontacté(e).");
      return;
    }

    setStatus("sending");
    try {
      const res = await fetch(`${API_BASE}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nom: form.nom,
          email: form.email,
          entreprise: form.entreprise,
          telephone: form.telephone,
          message: form.message,
          source: "landing_demo_request",
          consentement_rgpd: true,
        }),
      });
      if (!res.ok) throw new Error("Échec de l'envoi");
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError("Une erreur est survenue. Réessaie dans un instant ou écris-nous directement.");
    }
  }

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="demo-modal-title">
      <div style={styles.modal}>
        <button style={styles.closeBtn} onClick={onClose} aria-label="Fermer">
          ✕
        </button>

        {status === "success" ? (
          <div style={styles.successBox}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
            <h3 style={styles.title}>Demande envoyée</h3>
            <p style={styles.subtitle}>
              Merci — on revient vers vous très rapidement pour caler un créneau de démo.
            </p>
            <button style={styles.primaryBtn} onClick={onClose}>
              Fermer
            </button>
          </div>
        ) : (
          <>
            <h3 id="demo-modal-title" style={styles.title}>
              Demander une démo
            </h3>
            <p style={styles.subtitle}>
              Laissez vos coordonnées, on vous recontacte pour caler un créneau.
            </p>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.row}>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="Nom"
                  required
                  autoComplete="name"
                  value={form.nom}
                  onChange={(e) => update("nom", e.target.value)}
                />
                <input
                  style={styles.input}
                  type="email"
                  placeholder="Email professionnel"
                  required
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                />
              </div>
              <div style={styles.row}>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="Entreprise"
                  autoComplete="organization"
                  value={form.entreprise}
                  onChange={(e) => update("entreprise", e.target.value)}
                />
                <input
                  style={styles.input}
                  type="tel"
                  placeholder="Téléphone (optionnel)"
                  autoComplete="tel"
                  value={form.telephone}
                  onChange={(e) => update("telephone", e.target.value)}
                />
              </div>
              <textarea
                style={styles.textarea}
                placeholder="Votre besoin en quelques mots (optionnel)"
                rows={3}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
              />

              <label style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={form.consentement_rgpd}
                  onChange={(e) => update("consentement_rgpd", e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span style={styles.checkboxText}>
                  J'accepte d'être recontacté(e) par ExecPro360 au sujet de ma demande.{" "}
                  <a href="/confidentialite" target="_blank" rel="noopener noreferrer" style={styles.link}>
                    En savoir plus
                  </a>
                  .
                </span>
              </label>

              {error && <p style={styles.errorText}>{error}</p>}

              <button
                type="submit"
                style={{ ...styles.primaryBtn, opacity: status === "sending" ? 0.7 : 1 }}
                disabled={status === "sending"}
              >
                {status === "sending" ? "Envoi…" : "Envoyer ma demande"}
              </button>

              <p style={styles.note}>
                Vos données ne sont utilisées que pour vous recontacter au sujet de cette demande.
                Aucun partage à des tiers.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
  },
  modal: {
    background: "#0d1117",
    border: "1px solid #30363d",
    borderRadius: 14,
    padding: 32,
    width: "100%",
    maxWidth: 460,
    position: "relative",
    color: "#f1f5f9",
  },
  closeBtn: {
    position: "absolute",
    top: 16,
    right: 16,
    background: "transparent",
    border: "none",
    color: "#8D97A8",
    fontSize: 18,
    cursor: "pointer",
  },
  title: { fontSize: 22, fontWeight: 700, marginBottom: 8 },
  subtitle: { fontSize: 14.5, color: "#8D97A8", marginBottom: 22 },
  form: { display: "flex", flexDirection: "column", gap: 12 },
  row: { display: "flex", gap: 10, flexWrap: "wrap" },
  input: {
    flex: "1 1 160px",
    minWidth: 0,
    background: "#17212F",
    border: "1px solid #26313F",
    borderRadius: 7,
    padding: "12px 14px",
    color: "#EDF0F4",
    fontSize: 14.5,
  },
  textarea: {
    background: "#17212F",
    border: "1px solid #26313F",
    borderRadius: 7,
    padding: "12px 14px",
    color: "#EDF0F4",
    fontSize: 14.5,
    resize: "vertical",
    fontFamily: "inherit",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    marginTop: 4,
  },
  checkboxText: { fontSize: 12.5, color: "#8D97A8", lineHeight: 1.5 },
  link: { color: "#3FD6C0", textDecoration: "underline" },
  errorText: { color: "#F87171", fontSize: 13 },
  primaryBtn: {
    marginTop: 6,
    padding: "13px",
    borderRadius: 8,
    border: "none",
    background: "#1a9e72",
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
  },
  note: { fontSize: 11.5, color: "#8D97A8", marginTop: 4 },
  successBox: { textAlign: "center", padding: "20px 0" },
};
